import { useDisclosure, extendTheme, ChakraProvider, Center, Box, Fade, Image } from "@chakra-ui/react";
import { PropsWithChildren, useState, useMemo, useCallback, useEffect, useContext, createContext } from "react";
import { Helmet } from "react-helmet";
import { ModalLoginAndPlan } from "../components/molecules/VirtualModal";
import { LoadingScreen } from "../components/organisms/LoadingScreen";
import { axiosInstance, setAccessToken, axiosInstanceV2, isAuthenticated } from "../service/axios";
import { getBenefits, getProductsBenefits } from "../service/club/api";
import { useProfile } from "./ProfileProvider";
import { parseAgreementToTheme } from "../service/club/utils";
import { spawnAppEvent } from "../service/utils";
import { IThemeContext, ILoadingContext, IBookstoreModules } from "../interfaces/types";
import { isMockCentralClienteEmail } from '../service/club/mockCentralCliente';
import pako from 'pako';
import { injectChatWidget } from '../utils/chatWidget';
import { ISubscription } from '../service/types';

const defaultThemeContextData: IThemeContext = {
  // Propriedades básicas
  id: null,
  name: '',
  slug: '',
  logo: '',
  colors: {
    primaryColor: '#747474',
    primaryFontColor: '#747474',
    secondaryColor: '#747474',
    secondaryFontColor: '#747474',
    menuColor: '#747474',
    menuFontColor: '#747474',
    graphColors: [],
  },
  banners: [],

  productData: {
    isProduct: false,
    hasSuperapp: false,
    hasBookstore: false,
    hasMagazine: false,
    hasAudioBook: false,
    hasBestSellers: false,
    isMagazine: false,
    isBookstore: false,
  },
  bookstoreProduct: undefined,
  bookstoreModules: {
    biblioteca: undefined,
    banca: undefined,
    audiobook: undefined,
    hasBestSellers: false,
  },
  // Configurações de módulos (defaults seguros)
  isNative: false,
  isApp: false,
  isIOS: false,
  localModule: false,
  exClientModule: false,
  cpfModule: false,
  digitalMagazinesModule: false,
  ecommerceModule: false,
  bePartnerModule: false,
  hasIntegratedPurchaseOnMobileStore: false,

  // Contato
  telephone: '(21) 97420-4416',
  cellphone: '',
  contactLink: '',

  // Dados dinâmicos
  benefits: [],
  isLoadingBenefits: false,
  metadata: {},

  // Funções essenciais
  openModalLogin: () => {},
  fetchBenefits: () => {},
  setLoading: () => {},
  updateTheme: () => {},
  resetTheme: () => {},
  getValidSubscriptions: () => [],

  // Propriedades internas
  email: 'suporte@partiu.com.br',
  allowCpfLoginMethod: false,
  allowPromocodeLoginMethod: false,
  isMenuOpen: false,
  setMenuOpen: () => {},
  bookstoreLabels: null,
  isSetup: false,
  recurringBook: true,

  //product
  isProductSuperApp: false,
  product: undefined,
  fetchProductsBenefits: () => {},
  productBenefits: [],
};

const ThemeContext = createContext(defaultThemeContextData);

/* eslint-disable @typescript-eslint/no-explicit-any */
// Lê metadata.<key>.value de forma case-insensitive (API pode entregar isBestSellers, hasMagazine, hasAudioBook em variantes).
function getMetaValue(product: any, key: string): string | undefined {
  if (!product?.metadata) return undefined;
  const target = key.toLowerCase();
  for (const metaKey of Object.keys(product.metadata)) {
    if (metaKey.toLowerCase() === target) return product.metadata[metaKey]?.value;
  }
  return undefined;
}

function deriveBookstoreFlags(products: any[]) {
  const hasSuperapp = products.some((p: any) => !!p?.default_product?.is_superapp);

  // Clubes podem ter mais de um produto de livro (ex: Biblioteca + Kids). A principal é a
  // marcada com metadata.primary; sem flag, o primeiro não-Infantil (setor 5 sozinho).
  const bookProducts = products.filter(
    (p: any) => p?.default_product?.is_bookstore && getMetaValue(p, 'hasMagazine') !== 'true',
  );
  const isKids = (p: any) => String(getMetaValue(p, 'sectors') ?? '').replace(/\s/g, '') === '[5]';
  const bibliotecaProduct =
    bookProducts.find((p: any) => getMetaValue(p, 'primary') === 'true') ||
    bookProducts.find((p: any) => !isKids(p)) ||
    bookProducts[0];
  const kidsProduct = bookProducts.find((p: any) => p !== bibliotecaProduct && isKids(p));
  const bancaProduct = products.find((p: any) => getMetaValue(p, 'hasMagazine') === 'true');
  const audiobookHostProduct = products.find((p: any) => getMetaValue(p, 'hasAudioBook') === 'true');

  const hasBestSellers = products.some((p: any) => getMetaValue(p, 'isBestSellers') === 'true');

  const bookstoreModules = {
    biblioteca: bibliotecaProduct
      ? {
          productId: bibliotecaProduct.id as number,
          product: bibliotecaProduct,
          hasBestSellers: getMetaValue(bibliotecaProduct, 'isBestSellers') === 'true',
          hasAudioBook: getMetaValue(bibliotecaProduct, 'hasAudioBook') === 'true',
        }
      : undefined,
    kids: kidsProduct ? { productId: kidsProduct.id as number, product: kidsProduct } : undefined,
    banca: bancaProduct
      ? { productId: bancaProduct.id as number, product: bancaProduct }
      : undefined,
    audiobook: audiobookHostProduct
      ? { productId: audiobookHostProduct.id as number, product: audiobookHostProduct }
      : undefined,
    hasBestSellers,
  };

  return {
    productData: {
      isProduct: true,
      hasSuperapp,
      hasBookstore: !!bibliotecaProduct,
      hasMagazine: !!bancaProduct,
      hasAudioBook: !!audiobookHostProduct,
      hasBestSellers,
      isMagazine: !!bancaProduct,
      isBookstore: !!bibliotecaProduct,
    },
    bookstoreProduct: bibliotecaProduct || bancaProduct, // legado p/ código antigo
    bookstoreModules,
  };
}

export function ThemeProvider({
  children,
  themeResolver,
}: { themeResolver: () => Promise<IThemeContext | null> } & PropsWithChildren) {
  const modalLogin = useDisclosure();
  const { profile, updateProfile } = useProfile();
  const [benefits, setBenefits] = useState<any>([]);
  const [productBenefits, setProductBenefits] = useState<any>([]);
  const [isLoadingBenefits, setIsLoadingBenefits] = useState(false);
  const [setupData, setSetupData] = useState({
    isSetup: false,
    animate: false,
    exit: false,
  });
  const [notFound, setNotFound] = useState(false);
  const [isLogged, setIsLogged] = useState(false);
  const [isNative, setIsNative] = useState(true);
  const [productData, setProductData] = useState<{
    isProduct: boolean;
    hasSuperapp: boolean;
    hasBookstore: boolean;
    hasMagazine: boolean;
    hasAudioBook: boolean;
    hasBestSellers: boolean;
    isMagazine: boolean;
    isBookstore: boolean;
  }>({
    isProduct: false,
    hasSuperapp: false,
    hasBookstore: false,
    hasMagazine: false,
    hasAudioBook: false,
    hasBestSellers: false,
    isMagazine: false,
    isBookstore: false,
  });
  const [bookstoreProduct, setBookstoreProduct] = useState<any>(undefined);
  const [bookstoreModules, setBookstoreModules] = useState<IBookstoreModules>({
    biblioteca: undefined,
    banca: undefined,
    audiobook: undefined,
    hasBestSellers: false,
  });
  const [fetchBenefitsFlag, setFetchBenefitsFlag] = useState(false);
  const [urlData, setUrlData] = useState({ isApp: false, isIOS: false });
  const [data, setData] = useState<IThemeContext>(defaultThemeContextData);
  const [loadingData, setLoadingData] = useState<ILoadingContext>({
    isLoading: false,
    loadingDelay: 0,
    loadingDescription: 'Carregando...',
  });

  const theme = useMemo(
    () => {
      const checkboxPrimaryColor = data.colors.primaryColor || '#2B6CB0';

      return extendTheme({
        fonts: {
          heading: `'DM Sans', sans-serif;`,
          body: `'DM Sans', sans-serif;`,
        },
        colors: {
          ...data.colors,
        },
        components: {
          Checkbox: {
            defaultProps: {
              size: 'md',
            },
            baseStyle: {
              control: {
                borderColor: checkboxPrimaryColor,
                borderWidth: '1px',
                borderRadius: '4px',
                _checked: {
                  bg: checkboxPrimaryColor,
                  borderColor: checkboxPrimaryColor,
                  color: '#FFFFFF',
                },
                _hover: {
                  borderColor: checkboxPrimaryColor,
                },
                _focusVisible: {
                  boxShadow: `0 0 0 2px ${checkboxPrimaryColor}33`,
                },
              },
              label: {
                fontWeight: 400,
              },
            },
          },
        },
      });
    },
    [data]
  );

  const updateTheme = useCallback(
    (newThemeData: IThemeContext) => {
      setData({ ...data, ...newThemeData });
    },
    [data, setData]
  );

  const fetchModulesBenefits = async (agreementId: number) => {
    setIsLoadingBenefits(true);
    try {
      const benefitsArray = await getBenefits({
        agreement_id: agreementId,
      });

      const blobUrls = await Promise.all(
        benefitsArray.map(async (category: any) => {
          const processedModules = await Promise.all(
            category.modules
              .filter((mod: any) => mod.active)
              .map(async (module: any) => {
                module.is_gif = module.icon?.includes('.gif');

                try {
                  const response = await fetch(module.icon);
                  const blob = await response.blob();
                  module.icon = URL.createObjectURL(blob);
                } catch (error) {}

                return module;
              })
          );

          return {
            ...category,
            modules: processedModules,
          };
        })
      );
      setBenefits([...blobUrls]);
    } finally {
      setIsLoadingBenefits(false);
    }
  };

  const fetchProductsBenefits = async (agreementId: number) => {
    const cacheKey = `productBenefits:${agreementId}`;
    let hadCache = false;

    try {
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (Array.isArray(parsed) && parsed.length > 0) {
          hadCache = true;
          setProductBenefits(parsed);
          const derived = deriveBookstoreFlags(parsed);
          setProductData(derived.productData);
          setBookstoreProduct(derived.bookstoreProduct);
          setBookstoreModules(derived.bookstoreModules);
        }
      }
    } catch (error) {
      // cache inválido — ignora e revalida
    }

    if (!hadCache) setIsLoadingBenefits(true);
    try {
      const response = await getProductsBenefits({
        agreement_id: agreementId,
      });

   

      const productsWithBlobIcons = await Promise.all(
        response.map(async (product: any) => {
          try {
            if(product.metadata?.logo?.value) {
              const response = await fetch(product.metadata.logo.value);
              const blob = await response.blob();
              product.metadata.logo.value = URL.createObjectURL(blob);
            }
          } catch (error) {}

          return product;
        })
      );

      setProductBenefits([...productsWithBlobIcons]);

      const derived = deriveBookstoreFlags(productsWithBlobIcons);
      setProductData(derived.productData);
      setBookstoreProduct(derived.bookstoreProduct);
      setBookstoreModules(derived.bookstoreModules);

      try {
        sessionStorage.setItem(cacheKey, JSON.stringify(productsWithBlobIcons));
      } catch (error) {
        // quota excedida ou indisponível — sem cache
      }
    } finally {
      setIsLoadingBenefits(false);
    }
  };

  const fetchBenefits = async (agreementId: number) => {
    if (data?.isProductSuperApp) {
      fetchProductsBenefits(agreementId);
    } else {
      fetchModulesBenefits(agreementId);
    }
  };

  function getQueryParam(name: string) {
    const params = new URLSearchParams(window.location.search);
    return params.get(name);
  }

  const base64Data = getQueryParam('data');

  useEffect(() => {
    if (data.logo || notFound) {
      const splash = document.getElementById('html-splash');
      if (splash) {
        splash.style.opacity = '0';
        setTimeout(() => splash.remove(), 500);
      }
    }
  }, [data.logo, notFound]);

  useEffect(() => {
    if (!profile) {
      setIsLogged(false);
    } else {
      setIsLogged(true);
    }
  }, [profile]);

  useEffect(() => {
    if (!fetchBenefitsFlag && isLogged && data?.id) {
      setFetchBenefitsFlag(true);
      fetchBenefits(data?.id);
    } else if (!isLogged && data?.id) {
      fetchBenefits(data?.id);
    }
  }, [fetchBenefitsFlag, isLogged, data]);

  useEffect(() => {
    const params = Object.fromEntries(new URLSearchParams(location.search));
    const urlData = {
      isApp: params.isApp === 'true',
      isIOS: params.isIOS === 'true',
    };
    const existingUrlData = JSON.parse(localStorage.getItem('urlData') || 'null');

    if (existingUrlData) {
      if (!params.isApp) urlData['isApp'] = existingUrlData['isApp'];
      if (!params.isIOS) urlData['isIOS'] = existingUrlData['isIOS'];
    }

    if (params.isNative) {
      setIsNative(true);
    } else {
      setIsNative(false);
    }

    localStorage.setItem('urlData', JSON.stringify(urlData));

    setUrlData(urlData);
  }, []);

  useEffect(() => {
    const params = Object.fromEntries(new URLSearchParams(location.search));
    const urlData = {
      isApp: params.isApp === 'true',
      isIOS: params.isIOS === 'true',
    };
    const existingUrlData = JSON.parse(localStorage.getItem('urlData') || 'null');

    if (existingUrlData) {
      if (!params.isApp) urlData['isApp'] = existingUrlData['isApp'];
      if (!params.isIOS) urlData['isIOS'] = existingUrlData['isIOS'];
    }

    localStorage.setItem('urlData', JSON.stringify(urlData));

    setUrlData(urlData);
  }, []);

  useEffect(() => {
    const token = getQueryParam('token');
    const tokenExpirationDate = getQueryParam('tokenExpirationDate');

    if (token) {
      setAccessToken(token, tokenExpirationDate);
      setIsLogged(true);
      updateProfile({});
    }
  }, []);

  useEffect(() => {
    if (base64Data) {
      setSetupData({ ...setupData, isSetup: true, animate: false, exit: true });
      const binaryData = Uint8Array.from(atob(base64Data), (c) => c.charCodeAt(0));

      const jsonData = pako.inflate(binaryData, { to: 'string' }) as string;
      const agreement = JSON.parse(jsonData);
      const themeData = parseAgreementToTheme(agreement);
      setData(themeData);
      spawnAppEvent('splashDone');
      localStorage.setItem('theme', JSON.stringify(agreement));
      if (agreement.id) {
        axiosInstance.defaults.headers['x-agreement-id'] = agreement.id?.toString();
        axiosInstanceV2.defaults.headers['x-agreement-id'] = agreement.id?.toString();
      }
      if (agreement.token) {
        setAccessToken(agreement.token, agreement.tokenExpirationDate);
        setIsLogged(true);
      }
    } else {
      const legal = window.location.pathname.includes('/legal/');
      if (legal) {
        setSetupData({ ...setupData, isSetup: false, animate: false, exit: true });
      } else {
        setSetupData({ ...setupData, isSetup: false, animate: false, exit: false });
      }

      themeResolver().then((agreement: IThemeContext | null) => {
        if (!agreement) {
          setNotFound(true);
          return;
        }
        if(agreement.active === false) {
          setNotFound(true);
          return;
        }
        setData(agreement);
        spawnAppEvent('splashDone');
        localStorage.setItem('theme', JSON.stringify(agreement));
        if (agreement.id) localStorage.setItem('agreementId', agreement.id.toString());
        if (isAuthenticated()) {
          setIsLogged(true);
        }

        if (!legal) {
          setTimeout(() => setSetupData({ ...setupData, animate: true }), 1500);
        }
        setTimeout(() => setSetupData({ ...setupData, exit: true }), 2500);
      });
    }
  }, [themeResolver, base64Data]);

  useEffect(() => {
    injectChatWidget(data.chatWidgetScript);
  }, [data.chatWidgetScript]);

  return (
    <ThemeContext.Provider
      value={{
        ...data,
        ...urlData,
        ...(isMockCentralClienteEmail(profile?.email) && { customerCentralModule: true }),
        productData,
        bookstoreProduct,
        bookstoreModules,
        benefits,
        productBenefits,
        isLoadingBenefits,
        isNative,
        fetchBenefits,
        openModalLogin: modalLogin.onOpen,
        setLoading: (value: boolean, description: string = '', delay: number = loadingData.loadingDelay) => {
          setLoadingData({ isLoading: value, loadingDescription: description, loadingDelay: delay });
        },
        setMenuOpen: (value: boolean) => setData({ ...data, isMenuOpen: value }),
        updateTheme,
        resetTheme: () => setData(defaultThemeContextData),
        getValidSubscriptions: (subscriptions: ISubscription[]) => {
          return subscriptions
            .filter((sub) => sub.plan.agreement.id === data.id)
            .sort((a, b) => a.plan.level - b.plan.level);
        },
      }}
    >
      <ChakraProvider theme={theme}>
        <LoadingScreen
          description={loadingData.loadingDescription}
          isOpen={loadingData.isLoading}
          loadingDelay={loadingData.loadingDelay}
        />
        <Helmet>
          <title>{data.name}</title>
          <meta name="description" content={'Plataforma de Vantagens - Partiu'} />
        </Helmet>

        {notFound && (
          <Center minH="100vh" bg="white" px={4}>
            <Box textAlign="center" py={8} px={8} borderRadius="lg" boxShadow="lg" maxW="md" w="100%" bg="white">
              <Image src="/partiu-hub.png" alt="Logo Partiu Hub" maxW={32} mb={8} mx="auto" />
              <Box fontSize="6xl" fontWeight="bold" color="#ed813a" mb={2} letterSpacing="tight">
                404
              </Box>
              <Box fontSize="2xl" color="#747474" fontWeight="semibold" mb={6}>
                Ops! Página não encontrada ou convênio desativado.
              </Box>
              <a href="https://partiu.com.br/" style={{ textDecoration: 'none' }}>
                <Box
                  as="span"
                  px={8}
                  py={3}
                  bg="#ed813a"
                  color="white"
                  borderRadius="md"
                  fontWeight="medium"
                  _hover={{ bg: '#ff944d', textDecoration: 'none' }}
                  transition="background 0.2s"
                  cursor="pointer"
                  display="inline-block"
                >
                  Entre em contato com o suporte
                </Box>
              </a>
            </Box>
          </Center>
        )}
        <Fade
          transition={{ exit: { duration: 0.5 }, enter: { duration: 1, delay: 0.5 } }}
          in={!!(data.logo && !setupData.exit)}
        >
          <Center
            alignContent={'center'}
            justifyContent={'center'}
            pos={'fixed'}
            left={'0px'}
            backgroundColor={'#F1F4F5'}
            top={'0px'}
            width="100%"
            height="100vh"
            pointerEvents={'none'}
            zIndex={1000}
          >
            <Box
              width={'50px'}
              height={'50px'}
              borderRadius={'100%'}
              pos={'absolute'}
              backgroundColor={data.colors.primaryColor}
              transition={'2s'}
              transform={setupData.animate ? 'scale(100)' : 'scale(0)'}
            />
            {data.logo && <Image maxWidth={'500px'} src={data.logo} w={'70%'} margin="auto" transition={'0.1s'} />}
          </Center>
        </Fade>
        {setupData.exit && (
          <Fade in={setupData.exit} transition={{ enter: { duration: 0.5, delay: 0 } }}>
            {children}
          </Fade>
        )}

        <ModalLoginAndPlan isOpen={modalLogin.isOpen} onClose={modalLogin.onClose} />
      </ChakraProvider>
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
